import { useLayoutEffect, useRef, useState } from 'react';
import { Tag, Tooltip } from '../../UI';
import { evaluateKpiSeverity } from './kpiSeverity';
import { TimeDistributionPopover } from './TimeDistributionPopover';
import type { ProfessionHours } from './types';
import styles from './WeekKpiCell.module.css';

interface ProfessionHoursRowProps {
  professions: readonly ProfessionHours[];
}

// Must match `.professionTag { width }` in WeekKpiCell.module.css.
const CHIP_WIDTH = 30;
const CHIP_GAP = 3;
const ROW_PADDING = 4;
const MAX_ROWS = 2;
// Profession.code is BE-typed as `string` but the chip layout assumes a
// 3-char code. Slicing here keeps the chip readable if BE ever returns longer
// codes; the popover still uses the full `profession.name`.
const CODE_MAX_CHARS = 3;

const severityClass = (entry: ProfessionHours): string => {
  const severity = evaluateKpiSeverity(entry.planned, entry.threshold);
  if (severity === 'EXCEEDED') return styles.tagError;
  if (severity === 'WARNING') return styles.tagWarning;
  return '';
};

export const ProfessionHoursRow = ({ professions }: ProfessionHoursRowProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(professions.length);
  const professionsKey = professions.map((p) => p.profession.id).join(',');

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const compute = () => {
      const usable = el.clientWidth - ROW_PADDING;
      if (usable <= 0) return;
      const chipsPerRow = Math.max(1, Math.floor((usable + CHIP_GAP) / (CHIP_WIDTH + CHIP_GAP)));
      const capacity = chipsPerRow * MAX_ROWS;
      setVisibleCount(
        professions.length > capacity ? capacity - 1 : professions.length,
      );
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [professionsKey, professions]);

  const visible = professions.slice(0, visibleCount);
  const hiddenCount = professions.length - visibleCount;

  const chips = (
    <div ref={containerRef} className={styles.employeeHourTags}>
      {visible.map((entry) => (
        <Tag
          key={entry.profession.id}
          label={entry.profession.code.slice(0, CODE_MAX_CHARS)}
          size="small"
          className={`${styles.professionTag} ${severityClass(entry)}`}
        />
      ))}
      {hiddenCount > 0 && (
        <Tag label={`+${hiddenCount}`} size="small" className={styles.professionTag} />
      )}
    </div>
  );

  // Suppress the popover only when there are no professions at all. Weeks with
  // 0 planned hours still show it — planned vs. required capacity is
  // meaningful information either way.
  const isPopoverDisabled = professions.length === 0;

  return (
    <Tooltip
      trigger={chips}
      content={<TimeDistributionPopover professions={professions} />}
      disabled={isPopoverDisabled}
      triggerClassName={styles.employeeHourTagsTrigger}
    />
  );
};
